/**
 * Fire-and-forget click tracking for creator storefronts and products.
 *
 * Uses `navigator.sendBeacon` so the request never blocks navigation —
 * the click is recorded even when the user immediately leaves the page
 * (which is exactly what happens when they click a card to navigate).
 *
 * If sendBeacon isn't available or fails, falls back to a non-awaited
 * fetch with `keepalive: true`. Both paths swallow errors silently:
 * tracking failure must never break the user experience.
 *
 * Usage:
 *   import { trackStorefrontClick, trackProductClick } from '../utils/trackClick';
 *
 *   onClick={() => {
 *     trackStorefrontClick(creator.store_slug, 'home_grid');
 *     navigate(`/store/${creator.store_slug}`);
 *   }}
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _post = (path, payload) => {
  if (!API_URL) return;
  const url = `${API_URL}/api${path}`;
  const body = JSON.stringify(payload);

  // Preferred: sendBeacon — designed for exactly this case (telemetry
  // that must survive a navigation away from the page).
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
  } catch (_e) {
    // fall through to fetch
  }

  // Fallback: fetch with keepalive, no await, no error handling
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (_e) {
    /* never throw */
  }
};

export const trackStorefrontClick = (slug, source) => {
  if (!slug) return;
  _post('/track/storefront-click', { slug, source });
};

export const trackProductClick = (productId, source) => {
  if (!productId) return;
  _post('/track/product-click', { product_id: productId, source });
};
